import {
  Component,
  Element,
  Event,
  Method,
  Prop,
  Watch,
  h,
  Listen,
  ComponentInterface,
  EventEmitter,
} from '@stencil/core';
import { getActiveElement } from '../../utils/active-element';
import Popover, { PopoverPlacement } from '../../utils/popover';
import { getNearestTabbableElement } from '../../utils/tabbable';
import type { NavItemEventDetail } from '../../interface';

let dropDownIds = 0;

/**
 * Dropdowns show additional content in a panel.
 * Designed to work well with nano-menu components to provide a list of options (`nano-nav-items` / `nano-option`).
 * @slot trigger - The dropdown's trigger.
 * @slot - The dropdown's content.
 */
@Component({
  tag: 'nano-dropdown',
  styleUrl: 'dropdown.scss',
  shadow: true,
})
export class Dropdown implements ComponentInterface {
  private dropdownId = `dropdown-${dropDownIds++}`;
  private labelId = this.dropdownId + '-title';
  private ignoreOpenWatcher = false;
  private panel: HTMLElement;
  private popover: Popover;
  private trigger: HTMLElement;
  private positioner: HTMLElement;
  private accessibleTrigger: HTMLElement;
  private didLoad = false;
  private get menu() {
    return this.host.querySelector('nano-menu') as HTMLNanoMenuElement;
  }

  @Element() host: HTMLNanoDropdownElement;

  /** Determines if the dropdown should open automatically when the trigger is clicked */
  @Prop() autoOpen = true;

  /** Indicates whether or not the dropdown is open. You can use this in lieu of the show/hide methods. */
  @Prop({ mutable: true, reflect: true }) open = false;

  @Watch('open')
  handleOpenChange() {
    if (this.ignoreOpenWatcher) return;

    this.open ? this.show() : this.hide();

    if (this.accessibleTrigger) {
      this.accessibleTrigger.setAttribute(
        'aria-expanded',
        this.open.toString()
      );
      return;
    }

    this.updateAccessibleTrigger();
  }

  /**
   * Determines whether the dropdown should hide when a menu item is selected.
   */
  @Prop() closeOnSelect = true;

  /**
   * The dropdown will close when the user interacts outside of this element (e.g. clicking).
   */
  @Prop({ mutable: true }) containingElement: HTMLElement;

  /**
   * If you don't want to use the trigger slot, you can use this to 'tether' the dropdown to another element.
   * And you will still need to control when the component is opened (using show() / hide() or the `open` prop)
   */
  @Prop() tetherTo: HTMLElement = null;

  @Watch('tetherTo')
  handleTetherToChange() {
    this.createPopover();
    this.updateAccessibleTrigger();
  }

  /**
   * The preferred placement of the dropdown panel. Note that the actual placement may vary as needed to keep the panel
   * inside of the viewport.
   */
  @Prop() placement: PopoverPlacement = 'bottom-start';

  /**
   * The distance in pixels from which to offset the panel away from its trigger.
   */
  @Prop() distance = 2;

  /**
   * The distance in pixels from which to offset the panel along its trigger.
   */
  @Prop() skidding = 0;

  /**
   * Enable this option to prevent the panel from being clipped when the component is placed inside a container with
   * `overflow: auto|scroll`.
   */
  @Prop() hoist = false;

  @Watch('placement')
  @Watch('distance')
  @Watch('skidding')
  @Watch('hoist')
  handlePopoverOptionsChange() {
    this.popover.setOptions({
      strategy: this.hoist ? 'fixed' : 'absolute',
      placement: this.placement,
      skidding: this.skidding,
      distance: this.distance,
    });
  }

  /**
   * Title used to describe the dropdown content for accessibility
   */
  @Prop() dialogTitle!: string;

  // Events

  /**
   * Emitted when the dropdown opens. Calling `event.preventDefault()` will prevent it from being opened.
   */
  @Event() nanoShow: EventEmitter;

  /**
   * Emitted after the dropdown opens and all transitions are complete.
   */
  @Event() nanoAfterShow: EventEmitter;

  /**
   * Emitted when the dropdown closes. Calling `event.preventDefault()` will prevent it from being closed.
   */
  @Event() nanoHide: EventEmitter;

  /**
   * Emitted after the dropdown closes and all transitions are complete.
   */
  @Event() nanoAfterHide: EventEmitter;

  // this is a massive hack to make stencil expose the `EventEmitter` type in the final build
  // because the `Dropdown` class is used as a type in `nano-datalist`
  /** @internal */
  @Prop() eventType: EventEmitter;

  // Listeners

  @Listen('nanoOpen')
  secondaryOpen(ev: CustomEvent) {
    if (!(ev.detail as NavItemEventDetail).secondaryMenu) return;
    ev.stopPropagation();

    this.panel.classList.add('loading');
    this.panel.addEventListener(
      'transitionend',
      () => {
        this.panel.classList.remove('loading');
      },
      { once: true }
    );

    this.panel.style.minHeight =
      (ev.detail as NavItemEventDetail).secondaryMenu.scrollHeight + 'px';
  }

  @Listen('nanoClose')
  secondaryClose(ev: CustomEvent) {
    if (!(ev.detail as NavItemEventDetail).secondaryMenu) return;
    ev.stopPropagation();
    if (!(ev.target as HTMLElement).parentElement) return;
    this.panel.style.minHeight =
      (ev.target as HTMLElement).parentElement.scrollHeight + 'px';
  }

  @Listen('nanoSelect')
  handlePanelSelect(event: CustomEvent) {
    const target = event.target as HTMLElement;

    // Hide the dropdown when a menu item is selected
    if (this.closeOnSelect && target.tagName.toLowerCase() === 'nano-menu')
      this.hide();
  }

  // Methods

  /** Shows the dropdown panel */
  @Method()
  async show() {
    this.ignoreOpenWatcher = true;
    this.open = true;

    const nanoShow = this.nanoShow.emit();

    if (nanoShow.defaultPrevented) {
      this.open = false;
      this.ignoreOpenWatcher = false;
      return;
    }

    this.popover.show();
    this.ignoreOpenWatcher = false;

    document.addEventListener('mousedown', this.handleDocumentMouseDown);
    document.addEventListener('keydown', this.handleDocumentKeyDown);
  }

  /** Hides the dropdown panel */
  @Method()
  async hide() {
    this.ignoreOpenWatcher = true;
    this.open = false;

    const nanoHide = this.nanoHide.emit();

    if (nanoHide.defaultPrevented) {
      this.open = true;
      this.ignoreOpenWatcher = false;
      return;
    }

    if (this.popover) this.popover.hide();
    this.ignoreOpenWatcher = false;

    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
    document.removeEventListener('keydown', this.handleDocumentKeyDown);

    if (this.accessibleTrigger && this.focusEleInDropDwn()) {
      this.accessibleTrigger.focus();
    }
  }

  // Private methods

  /**
   * Decides if the current active element or element
   * connected to the current event is connected to the Dropdown
   * @param e
   * @returns whether the current focus is within the dropdown
   */
  private focusEleInDropDwn(e?: Event) {
    const activeElement = getActiveElement();

    if (e && e.composedPath()?.length) {
      const ddInPath = e.composedPath().includes(this.containingElement);
      return !e.composedPath().includes(this.accessibleTrigger) && ddInPath;
    }

    return (
      activeElement &&
      activeElement.closest(this.containingElement.tagName.toLowerCase()) ===
        this.containingElement &&
      activeElement !== this.accessibleTrigger
    );
  }

  private togglePanel = () => {
    if (!this.autoOpen) return;
    this.open ? this.hide() : this.show();
  };

  // Slotted triggers can be arbitrary content, but we need to link them to the dropdown panel with `aria-haspopup` and
  // `aria-expanded`. These must be applied to the "accessible trigger" (the tabbable portion of the trigger element
  // that gets slotted in) so screen readers will understand them. The accessible trigger could be the slotted element,
  private updateAccessibleTrigger() {
    if (!this.didLoad) return;

    let accessibleTrigger: HTMLElement;
    this.accessibleTrigger = null;

    if (!this.tetherTo) {
      const assignedElements = Array.from(
        this.host.querySelectorAll('[slot="trigger"]')
      );
      accessibleTrigger = assignedElements.map(getNearestTabbableElement)[0];
    } else {
      accessibleTrigger = getNearestTabbableElement(this.tetherTo);
    }

    if (accessibleTrigger) {
      accessibleTrigger.setAttribute('aria-haspopup', 'true');
      accessibleTrigger.setAttribute(
        'aria-expanded',
        this.open ? 'true' : 'false'
      );
      this.accessibleTrigger = accessibleTrigger;
    }
  }

  // Handlers

  private handleDocumentKeyDown(event: KeyboardEvent) {
    // Close when escape is pressed
    if (event.key === 'Escape') {
      this.hide();
      return;
    }

    // Close when tabbing results in the focus leaving the close element
    if (event.key === 'Tab') {
      setTimeout(() => {
        if (
          document.activeElement &&
          document.activeElement.closest(
            this.containingElement.tagName.toLowerCase()
          ) !== this.containingElement
        ) {
          this.hide();
          return;
        }
      });
    }

    // If a menu is present, focus on it when certain keys are pressed
    if (this.menu && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      // must have menu item, must have pressed down, must be open and must not have focus within dd
      if (!this.open || this.focusEleInDropDwn(event)) return;
      event.preventDefault();
      this.menu.setFocus();
      return;
    }

    // All other keys focus the menu and pass the event through to menu (necessary for type-to-search to work)
    const ePath = event.composedPath();
    if (this.menu && ePath.length && !ePath.find((el) => el !== this.menu)) {
      this.menu.dispatchEvent(new KeyboardEvent(event.type, event));
      return;
    }
  }

  private handleDocumentMouseDown(event: MouseEvent) {
    // Close when clicking outside of the close element

    if (!this.focusEleInDropDwn(event)) {
      this.hide();
      return;
    }
  }

  private handleTriggerKeyDown = (event: KeyboardEvent) => {
    // Open the panel when pressing down or up while focused on the trigger
    if (['ArrowDown', 'ArrowUp', ' '].includes(event.key)) {
      this.show();
      event.preventDefault();
      event.stopPropagation();

      if (this.menu) this.menu.setFocus();
    }
  };

  private handleTriggerSlotChange = () => {
    this.updateAccessibleTrigger();
  };

  private createPopover() {
    if (this.popover) {
      if (this.open) this.hide();
      this.popover.destroy();
      this.popover = null;
    }

    this.popover = new Popover(this.tetherTo || this.trigger, this.positioner, {
      strategy: this.hoist ? 'fixed' : 'absolute',
      placement: this.placement,
      distance: this.distance,
      skidding: this.skidding,
      transitionElement: this.panel,
      onAfterHide: () => this.nanoAfterHide.emit(),
      onAfterShow: () => this.nanoAfterShow.emit(),
      onTransitionEnd: () => {
        if (!this.open) {
          this.panel.scrollTop = 0;
        } else if (this.menu) {
          this.menu.showActiveElement();
        }
      },
    });
    // Show on init if open
    if (this.open) {
      this.show();
    }
  }

  // Stencil hooks

  connectedCallback() {
    this.handleDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
    this.handleDocumentMouseDown = this.handleDocumentMouseDown.bind(this);

    if (!this.containingElement) this.containingElement = this.host;
  }

  componentDidLoad() {
    this.didLoad = true;
    this.createPopover();
    setTimeout(() => this.updateAccessibleTrigger(), 100);
  }

  disconnectedCallback() {
    this.hide();
    if (this.popover) this.popover.destroy();
  }

  render() {
    return (
      <div
        part="base"
        id={this.dropdownId}
        class={{
          dropdown: true,
          'dropdown--open': this.open,
        }}
      >
        <span
          part="trigger"
          class="dropdown__trigger"
          ref={(el) => (this.trigger = el)}
          onKeyDown={this.handleTriggerKeyDown}
          onClick={this.togglePanel}
        >
          <slot name="trigger" onSlotchange={this.handleTriggerSlotChange} />
        </span>

        <div ref={(el) => (this.positioner = el)} class="dropdown__positioner">
          <div
            ref={(el) => (this.panel = el)}
            part="panel"
            class={`dropdown__panel ${this.placement.split('-').join(' ')}`}
            aria-hidden={!this.open}
            role="dialog"
            aria-modal="true"
            aria-labelledby={this.dialogTitle ? this.labelId : undefined}
          >
            {this.dialogTitle && (
              <h2
                id={this.labelId}
                class="dropdown__accessible-title"
                aria-live="polite"
              >
                {this.dialogTitle}
              </h2>
            )}
            <slot />
          </div>
        </div>
      </div>
    );
  }
}
